import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageSquare, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import DocumentTitle from "@/components/DocumentTitle";
import { useAuth } from "@/components/AuthProvider";
import { client } from "@/lib/api";
import SignInPrompt from "@/pages/member/SignInPrompt";
import LevelIcon from "@/components/LevelIcon";

/**
 * Wave C.4 (Vania 7 Oct 2026) · invite typeahead row. Types from the first
 * character and matches by name OR @handle. Shows up to 6 matches. Mutual
 * followers (if the hint is in the payload) sort first. Tap a result to
 * fill the input and queue the invite. Keyboard: Enter sends; Esc clears.
 */
function InviteTypeaheadRow({ value, onChange, onInvite, busy }) {
  const [results, setResults] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [focusIdx, setFocusIdx] = React.useState(-1);
  const query = (value || "").trim().replace(/^@+/, "");

  React.useEffect(() => {
    if (!query) { setResults([]); return; }
    let dead = false;
    const t = setTimeout(() => {
      client.get("/people/search", { params: { q: query, limit: 6 } })
        .then((r) => { if (!dead) setResults(r.data?.people || []); })
        .catch(() => { if (!dead) setResults([]); });
    }, 120);
    return () => { dead = true; clearTimeout(t); };
  }, [query]);

  const CARD = "var(--ww-surface)";
  const LINE_C = "var(--ww-line)";
  const META_C = "var(--ww-text-muted)";
  const Y = "#FFD60A";

  const pick = (p) => {
    onChange(p.handle || "");
    setOpen(false);
    setFocusIdx(-1);
  };

  return (
    <div className="rounded-2xl p-5 mt-4" data-testid="squad-detail-add-row"
         style={{ background: CARD, border: `1px solid ${LINE_C}`, position: "relative" }}>
      <p style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                  textTransform: "uppercase", letterSpacing: "0.14em", color: META_C }}>
        invite a member
      </p>
      <p className="mt-1" style={{ fontFamily: "Arial", fontSize: 12, color: META_C }}>
        start typing a name or handle · nobody lands in a squad without tapping accept · squads cap at 12.
      </p>
      <div className="flex gap-2 mt-3" style={{ position: "relative" }}>
        <input value={value}
               onChange={(e) => { onChange(e.target.value); setOpen(true); setFocusIdx(-1); }}
               onFocus={() => setOpen(true)}
               placeholder="start typing · @handle or name"
               data-testid="squad-invite-typeahead-input"
               aria-autocomplete="list" aria-expanded={open}
               onKeyDown={(e) => {
                 if (e.key === "Enter") {
                   if (focusIdx >= 0 && results[focusIdx]) pick(results[focusIdx]);
                   else onInvite();
                 } else if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => Math.min(results.length - 1, i + 1)); }
                 else if (e.key === "ArrowUp")   { e.preventDefault(); setFocusIdx((i) => Math.max(-1, i - 1)); }
                 else if (e.key === "Escape")    { setOpen(false); }
               }}
               style={{ flex: 1, padding: 12, borderRadius: 10,
                        background: "var(--ww-sunken)", border: `1px solid ${LINE_C}`,
                        color: "var(--ww-text)", fontFamily: "Arial", fontSize: 14, outline: "none" }} />
        <button onClick={() => { setOpen(false); onInvite(); }} disabled={!value.trim() || busy}
                data-testid="squad-detail-add-btn"
                style={{ padding: "10px 18px", borderRadius: 999,
                         background: value.trim() ? Y : CARD,
                         color: value.trim() ? "#1A1A30" : META_C,
                         border: `1px solid ${value.trim() ? Y : LINE_C}`,
                         fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
                         textTransform: "lowercase",
                         display: "inline-flex", alignItems: "center", gap: 6,
                         cursor: value.trim() ? "pointer" : "not-allowed" }}>
          <UserPlus size={14} /> invite
        </button>
      </div>
      {open && results.length > 0 && (
        <ul data-testid="squad-invite-typeahead-results"
            style={{ position: "absolute", top: "calc(100% - 8px)", left: 20, right: 20,
                     zIndex: 10, maxHeight: 260, overflowY: "auto",
                     background: CARD, border: `1px solid ${LINE_C}`, borderRadius: 12,
                     listStyle: "none", padding: 4, margin: 0,
                     boxShadow: "0 10px 30px rgba(0,0,0,0.25)" }}>
          {results.map((p, i) => (
            <li key={p.user_id || p.handle}>
              <button type="button"
                      data-testid={`squad-invite-typeahead-result-${p.handle}`}
                      onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                      style={{ width: "100%", display: "flex", alignItems: "center", gap: 10,
                               padding: "10px 10px", borderRadius: 10, border: 0,
                               background: i === focusIdx ? "var(--ww-sunken)" : "transparent",
                               color: "var(--ww-text)", cursor: "pointer", textAlign: "left" }}>
                {p.picture ? (
                  <img src={p.picture} alt="" style={{ width: 32, height: 32, borderRadius: 999, objectFit: "cover", flexShrink: 0 }} />
                ) : (
                  <span style={{ width: 32, height: 32, borderRadius: 999, background: "var(--ww-sunken)",
                                  display: "grid", placeItems: "center", color: "var(--ww-text)",
                                  fontFamily: "Poppins", fontWeight: 800, fontSize: 13, flexShrink: 0 }}>
                    {((p.display_name || p.handle || "?")[0] || "?").toLowerCase()}
                  </span>
                )}
                <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                  <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                                  textTransform: "lowercase",
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {(p.display_name || p.handle || "someone").toLowerCase()}
                  </span>
                  <span style={{ fontFamily: "Arial", fontSize: 12, color: META_C,
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    @{p.handle}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Wave 15 Step 6 (Vania 3 Oct 2026) · Squad detail page.
 *
 * Route: /app/squad/:squadId
 *
 *  · Shows the squad name + member list.
 *  · Owner-only direct-add input (per D53 · squads are joined only by being
 *    added · POST /api/squads/{id}/add). Non-owners see the list only.
 *  · "Open yap" opens the squad thread at /app/yap/squad:<id>.
 *  · Does NOT render invite-based flows (those are held per Step 8).
 */
const META = "var(--ww-text-muted)";
const LINE = "var(--ww-line)";
const CARD = "var(--ww-surface)";
const YELLOW = "#FFD60A";

export default function SquadDetail() {
  const { squadId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState(false);
  // Wave C · sender outbox / "waiting to join" list.
  const [sent, setSent] = useState([]);
  const [cancelId, setCancelId] = useState(null);

  const load = () => {
    client.get(`/squads/${squadId}`).then((r) => setData(r.data))
      .catch((e) => {
        if (e?.response?.status === 404 || e?.response?.status === 403) setNotFound(true);
      });
  };
  const loadSent = () => {
    client.get(`/squads/${squadId}/invites/sent`)
      .then((r) => setSent(r.data?.invites || []))
      .catch(() => setSent([]));
  };

  useEffect(() => {
    if (!user) return;
    load();
    loadSent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, squadId]);

  if (!authLoading && !user) {
    return <SignInPrompt title="squad"
                         line="sign in to view this squad."
                         next={`/app/squad/${squadId}`} testId="squaddetail-signin" />;
  }

  if (notFound) {
    return (
      <div className="flex h-full flex-col" data-testid="squad-detail-404"
           style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
        <HeaderBar nav={nav} title="not found" />
        <div className="flex-1 grid place-items-center text-center px-6">
          <div>
            <p style={{ fontFamily: "Arial", color: META }}>
              you're not in this squad, or it doesn't exist.
            </p>
            <Link to="/app/squad" data-testid="squad-detail-404-back"
                  style={{ display: "inline-block", marginTop: 12, padding: "10px 18px",
                           borderRadius: 999, background: CARD, border: `1px solid ${LINE}`,
                           color: "var(--ww-text)", textDecoration: "none",
                           fontFamily: "Poppins", fontWeight: 600, textTransform: "lowercase" }}>
              back to squad
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex h-full flex-col"
           style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
        <HeaderBar nav={nav} title="loading" />
      </div>
    );
  }

  const isOwner = data.squad.owner_user_id === user?.user_id;
  const members = data.member_profiles || [];
  const memberCount = data.members?.length || members.length;

  const inviteAdd = async () => {
    const h = handle.trim().replace(/^@+/, "").toLowerCase();
    if (!h || busy) return;
    setBusy(true);
    try {
      const r = await client.post(`/squads/${squadId}/invite`, { invitee_handle: h });
      if (r.data.already_member) toast.success("already in the squad");
      else if (r.data.duplicate) toast.success("invite already sent");
      else toast.success("invite sent · they'll see it in their inbox");
      setHandle("");
      loadSent();
    } catch (e) {
      // Brand copy rule · never surface "user" to a member. Map known backend
      // detail tokens to lowercase, member-friendly strings before toasting.
      const d = e?.response?.data?.detail;
      const copy = {
        invitee_required: "enter a handle",
        user_required: "enter a handle",
        handle_required: "enter a handle",
        member_not_found: "no one on watu wako with that handle",
        user_not_found: "no one on watu wako with that handle",
        not_squad_owner: "only the owner can invite",
        not_a_squad_member: "only the owner can invite",
        cannot_invite_self: "that's you",
        squad_full: "squad is full · 12 is the cap",
        squad_not_found: "squad not found",
      };
      const safe = (typeof d === "string" && copy[d])
                      || (typeof d === "string" ? d.replace(/_/g, " ").replace(/\buser\b/g, "member")
                                                : "could not send invite");
      toast.error(safe);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col" data-testid="squad-detail"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title={`${data.squad.name} · squad · watu wako`} />
      <HeaderBar nav={nav} title={(data.squad.name || "squad").toLowerCase()} />

      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-10" style={{ overscrollBehavior: "contain" }}>
        <div className="mx-auto" style={{ maxWidth: 640 }}>
          <div className="rounded-2xl p-5"
               style={{ background: CARD, border: `1px solid ${LINE}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 44, height: 44, borderRadius: 999, background: "var(--ww-sunken)",
                             display: "grid", placeItems: "center" }}>
                <Users size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 20,
                             textTransform: "lowercase" }} data-testid="squad-detail-name">
                  {data.squad.name}
                </p>
                <p style={{ fontFamily: "Arial", fontSize: 13, color: META }}>
                  {memberCount} {memberCount === 1 ? "member" : "members"}
                </p>
              </div>
              <Link to={`/app/yap/${encodeURIComponent("squad:" + squadId)}`}
                    data-testid="squad-detail-open-yap"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6,
                             padding: "10px 14px", borderRadius: 999, background: YELLOW,
                             color: "#1A1A30", textDecoration: "none",
                             fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
                             textTransform: "lowercase" }}>
                <MessageSquare size={14} /> open yap
              </Link>
            </div>
          </div>

          {isOwner && (
            <InviteTypeaheadRow
              value={handle}
              onChange={setHandle}
              onInvite={inviteAdd}
              busy={busy}
            />
          )}

          {sent.length > 0 && (
            <section className="mt-5" data-testid="squad-sent-invites">
              <p style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 12,
                           textTransform: "uppercase", letterSpacing: "0.18em", color: META,
                           paddingLeft: 4 }}>
                waiting to join · {sent.length}
              </p>
              <p className="mt-1" style={{ fontFamily: "Arial", fontSize: 12, color: META, paddingLeft: 4 }}>
                invites you or another member sent · they expire 14 days after sending.
              </p>
              <ul className="mt-2" data-testid="squad-sent-list">
                {sent.map((inv) => {
                  const p = inv.invitee || {};
                  const senderIsMe = inv.inviter_user_id === user?.user_id;
                  const canCancel = isOwner || senderIsMe;
                  const sendingThis = cancelId === inv.id;
                  return (
                    <li key={inv.id} data-testid={`squad-sent-${p.handle || inv.invitee_user_id}`}
                        style={{ display: "flex", alignItems: "center", gap: 12,
                                  padding: "12px 4px", borderBottom: `1px solid ${LINE}` }}>
                      <PersonAvatar p={p} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                                       textTransform: "lowercase",
                                       overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                       display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                            {(p.display_name || p.name || p.handle || "someone").toLowerCase()}
                          </span>
                          <LevelIcon user={p} hideBase />
                        </div>
                        <div style={{ fontFamily: "Arial", fontSize: 12, color: META,
                                       overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          @{p.handle || "—"} · pending{senderIsMe ? "" : " · sent by @" + (inv.inviter?.handle || "member")}
                        </div>
                      </div>
                      {canCancel && (
                        <button data-testid={`squad-sent-cancel-${inv.id}`}
                                disabled={sendingThis}
                                onClick={async () => {
                                  setCancelId(inv.id);
                                  try {
                                    await client.post(`/squads/invites/${inv.id}/cancel`);
                                    toast.success("invite cancelled");
                                    loadSent();
                                  } catch { toast.error("could not cancel"); }
                                  finally { setCancelId(null); }
                                }}
                                style={{ padding: "6px 12px", borderRadius: 999,
                                         background: "transparent", color: "var(--ww-text)",
                                         border: `1px solid ${LINE}`,
                                         fontFamily: "Poppins", fontWeight: 700, fontSize: 12,
                                         textTransform: "lowercase",
                                         cursor: sendingThis ? "wait" : "pointer" }}>
                          {sendingThis ? "…" : "cancel"}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <div className="mt-5">
            <p style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 12,
                         textTransform: "uppercase", letterSpacing: "0.18em", color: META,
                         paddingLeft: 4 }}>
              members {sent.length > 0 ? `· ${members.length} + ${sent.length} invited` : ""}
            </p>
            <ul className="mt-2" data-testid="squad-detail-members">
              {members.map((p) => (
                <li key={p.user_id} data-testid={`squad-member-${p.handle || p.user_id}`}
                    style={{ display: "flex", alignItems: "center", gap: 12,
                              padding: "12px 4px", borderBottom: `1px solid ${LINE}` }}>
                  <PersonAvatar p={p} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                                   textTransform: "lowercase" }}>
                      {(p.display_name || p.name || p.handle || "someone").toLowerCase()}
                    </div>
                    <div style={{ fontFamily: "Arial", fontSize: 12, color: META }}>
                      @{p.handle}
                    </div>
                  </div>
                  {p.user_id === data.squad.owner_user_id && (
                    <span style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 11,
                                   color: YELLOW, textTransform: "uppercase",
                                   letterSpacing: "0.14em" }}>owner</span>
                  )}
                </li>
              ))}
              {/* Vania 7 Oct 2026 F · pending invitees sit inline below the
                  real members so a squad with pending invites never looks
                  empty. "invited · {date}" label on pending rows. Failed /
                  expired rows get a resend button for the sender + owner. */}
              {sent.map((inv) => {
                const p = inv.invitee || {};
                const senderIsMe = inv.inviter_user_id === user?.user_id;
                const canAct = isOwner || senderIsMe;
                const failed = inv.status === "failed" || inv.last_error;
                const sentWhen = inv.created_at ? inv.created_at.slice(0, 10) : "";
                return (
                  <li key={`inv-${inv.id}`} data-testid={`squad-invitee-${p.handle || inv.invitee_user_id}`}
                      style={{ display: "flex", alignItems: "center", gap: 12,
                                padding: "12px 4px", borderBottom: `1px solid ${LINE}`,
                                opacity: 0.72 }}>
                    <PersonAvatar p={p} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                                     textTransform: "lowercase",
                                     display: "flex", alignItems: "center", gap: 6,
                                     overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                          {(p.display_name || p.name || p.handle || "someone").toLowerCase()}
                        </span>
                        <LevelIcon user={p} hideBase />
                      </div>
                      <div style={{ fontFamily: "Arial", fontSize: 12, color: META,
                                     overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        @{p.handle || "—"} · {failed ? "failed" : "invited"}
                        {sentWhen ? ` · ${sentWhen}` : ""}
                        {!senderIsMe && inv.inviter?.handle ? ` · by @${inv.inviter.handle}` : ""}
                      </div>
                    </div>
                    {canAct && failed && (
                      <button data-testid={`squad-invitee-resend-${inv.id}`}
                              onClick={async () => {
                                try {
                                  await client.post(`/squads/invites/${inv.id}/resend`);
                                  toast.success("invite resent");
                                  loadSent();
                                } catch { toast.error("could not resend"); }
                              }}
                              style={{ padding: "6px 12px", borderRadius: 999,
                                       background: YELLOW, color: "#1A1A30",
                                       border: `1px solid ${YELLOW}`,
                                       fontFamily: "Poppins", fontWeight: 700, fontSize: 12,
                                       textTransform: "lowercase", cursor: "pointer" }}>
                        resend
                      </button>
                    )}
                    {canAct && !failed && (
                      <button data-testid={`squad-invitee-cancel-${inv.id}`}
                              onClick={async () => {
                                setCancelId(inv.id);
                                try {
                                  await client.post(`/squads/invites/${inv.id}/cancel`);
                                  toast.success("invite cancelled");
                                  loadSent();
                                } catch { toast.error("could not cancel"); }
                                finally { setCancelId(null); }
                              }}
                              style={{ padding: "6px 12px", borderRadius: 999,
                                       background: "transparent", color: "var(--ww-text)",
                                       border: `1px solid ${LINE}`,
                                       fontFamily: "Poppins", fontWeight: 700, fontSize: 12,
                                       textTransform: "lowercase", cursor: "pointer" }}>
                        cancel
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeaderBar({ nav, title }) {
  return (
    <header className="flex items-center gap-3 px-4 py-3"
            style={{ borderBottom: `1px solid ${LINE}` }}>
      <button onClick={() => nav("/app/squad")} aria-label="back"
              data-testid="squad-detail-back"
              style={{ background: "transparent", border: 0, color: "var(--ww-text)",
                       cursor: "pointer", display: "flex", alignItems: "center" }}>
        <ArrowLeft size={20} />
      </button>
      <div style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 16,
                     textTransform: "lowercase" }}>{title}</div>
    </header>
  );
}

function PersonAvatar({ p }) {
  const url = p?.picture || p?.photo_url || "";
  if (url) return <img src={url} alt="" style={{ width: 40, height: 40, borderRadius: 999, objectFit: "cover" }} />;
  const letter = (p?.handle || p?.display_name || "?").slice(0, 1).toUpperCase();
  return (
    <div style={{ width: 40, height: 40, borderRadius: 999, background: "var(--ww-sunken)",
                   display: "grid", placeItems: "center", color: "var(--ww-text)",
                   fontFamily: "Poppins", fontWeight: 700 }}>{letter}</div>
  );
}
